// src/hooks/useBrand.js
// ---------------------------------------------------------------------------
// The brand for the activation a guest is in, kept live.
//
// Two subscriptions rather than one, and only when the second is needed:
//
//   the activation   always, because it carries its own brand and because
//                    PromotionScreen already needs it live
//   its campaign     only when the activation has an eventGroupId, so that a
//                    brand set once on a campaign reaches every activation in
//                    it without being copied onto each one
//
// Copying the campaign's brand onto each activation at save time would be one
// fewer read on the phone, and it would mean a client changing their colours
// silently missed every activation created before the change. A guest-facing
// screen that is wrong in a way nobody notices is worse than a second read.
//
// Fails to the SipNScore brand on any error. A guest must never be blocked, or
// shown a half-painted screen, because a colour could not be fetched.
// ---------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_BRAND, resolveBrand } from "../config/branding";

/**
 * Everything about an activation that the guest screens need to render, from
 * the two documents this hook already reads.
 *
 * `useBrand` is a wrapper over this. The guest fields need exactly the same
 * activation-then-campaign pair that the brand does, and opening a third and
 * fourth listener on a phone to fetch documents already in memory would be a
 * second's delay on the details screen for nothing.
 *
 * @returns {{brand: object, guestFields: Array|null}} guestFields is the raw
 *   stored array, already resolved through the campaign, or null when neither
 *   has one, which the config layer reads as "the original three".
 */
export function useActivationConfig(eventId) {
  const [event, setEvent] = useState(null);
  const [group, setGroup] = useState(null);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      setGroup(null);
      return undefined;
    }
    const stop = onSnapshot(
      doc(db, "events", eventId),
      (snap) => setEvent(snap.exists() ? snap.data() : null),
      () => setEvent(null)
    );
    return () => stop();
  }, [eventId]);

  const groupId = event?.eventGroupId || null;

  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      return undefined;
    }
    const stop = onSnapshot(
      doc(db, "eventGroups", groupId),
      (snap) => setGroup(snap.exists() ? snap.data() : null),
      () => setGroup(null)
    );
    return () => stop();
  }, [groupId]);

  if (!eventId) return { brand: DEFAULT_BRAND, guestFields: null };

  return {
    brand: resolveBrand(event, group),
    // The activation's own list, or its campaign's, or nothing. Resolving which
    // fields that means happens in the config layer, which is shared with the
    // portal and the server so all three agree.
    guestFields:
      (Array.isArray(event?.guestFields) && event.guestFields.length
        ? event.guestFields
        : null) ||
      (Array.isArray(group?.guestFields) && group.guestFields.length
        ? group.guestFields
        : null),
  };
}

/** Just the brand, for the callers that only ever wanted that. */
export function useBrand(eventId) {
  return useActivationConfig(eventId).brand;
}
